// Money → Bills & payments: the Payments due board (overdue, this week, deposits waiting, scheduled in Wise, later, to
// approve, recently paid), every bill, every payment, and Wise history (past transfers matched to bills and POs). All
// from synced rows through useBillIndex(), so tabs, filters and the bill panel are instant. ?tab=due|bills|payments|wise,
// ?vendor=<id>, ?run=<id>, ?bill=<id> opens a bill (in the side pane), ?new=1 starts a new bill.
import { FileArchive, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  BILL_KIND_LABELS,
  BILL_STATUS_LABELS,
  BILL_STATUS_TONE,
  BILL_STATUSES,
  type BillStatus,
  billDue,
  billLinks,
  billMatch,
  billState,
  MATCH_LABELS,
  MATCH_TONE,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_TONE,
  paymentWith,
  vendorLabelOf,
} from "../../../../shared/bills.ts";
import type { Bill, Payment } from "../../../../shared/db.gen.ts";
import { amount, dayOf, useBillIndex } from "../../lib/bills.ts";
import { shortDate } from "../../lib/costs.ts";
import { can } from "../../lib/mutate.ts";
import { Link, setSearch, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Select } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { usePaneParam, usePaneRoot } from "../../ui/SidePane.tsx";
import { DueBoard } from "./DueBoard.tsx";
import { NewBillDialog } from "./NewBillDialog.tsx";
import { WiseHistory } from "./WiseHistory.tsx";

const TABS = [
  { key: "due", label: "Payments due" },
  { key: "bills", label: "Bills" },
  { key: "payments", label: "Payments" },
  { key: "wise", label: "Wise history" },
] as const;
type Tab = (typeof TABS)[number]["key"];

export function BillsPage() {
  const params = useSearch();
  const tab = (TABS.find((t) => t.key === params.get("tab"))?.key ?? "due") as Tab;
  const vendorId = params.get("vendor") || null;
  const runId = params.get("run") || null;
  // Table views --- the Bills and Payments tables' View and Columns menus sit beside the tabs
  const [viewsSlot, setViewsSlot] = useState<HTMLDivElement | null>(null);
  // Side pane --- a bill opens in the side pane; ?bill=<id> (links, Ctrl+K, a new bill) opens it there too
  usePaneParam("bill", "bill");
  const ix = useBillIndex();
  const runs = useRows("production_run");
  const [creating, setCreating] = useState(params.get("new") === "1");
  // ?new=1 (Ctrl+K's New bill) opens the dialog even when the page is already open.
  const wantsNew = params.get("new") === "1";
  useEffect(() => {
    if (wantsNew) setCreating(true);
  }, [wantsNew]);
  const unmatched = useRows("wise_transfer").filter((t) => t.match_status === "suggested").length;

  const vendorOptions = useMemo(() => {
    const used = new Set([...ix.d.bills.map((b) => b.vendor_id), ...ix.d.pos.filter((p) => Number(p.deposit_pct) > 0).map((p) => p.vendor_id)]);
    return [
      { value: "", label: "All vendors" },
      ...ix.d.vendors
        .filter((v) => used.has(v.id) || v.id === vendorId)
        .map((v) => ({ value: v.id, label: vendorLabelOf(v) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [ix, vendorId]);
  const runOptions = useMemo(
    () => [
      { value: "", label: "All runs" },
      ...[...runs].sort((a, b) => b.number.localeCompare(a.number)).map((r) => ({ value: r.id, label: `${r.number}${r.name ? ` · ${r.name}` : ""}` })),
    ],
    [runs],
  );

  return (
    <div className="relative flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex min-h-12 shrink-0 flex-wrap items-center gap-1 border-b border-line bg-surface px-4 py-2">
          <h1 className="mr-3 text-[15px] font-semibold whitespace-nowrap">Bills & payments</h1>
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setSearch({ tab: t.key === "due" ? null : t.key })}
              className={cx(
                "flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[13px] whitespace-nowrap",
                tab === t.key ? "bg-accent-soft font-medium text-accent-text" : "text-muted hover:bg-hover hover:text-text",
              )}
            >
              {t.label}
              {t.key === "wise" && unmatched > 0 && <Badge tone="warn">{unmatched}</Badge>}
            </button>
          ))}
          {(tab === "bills" || tab === "payments") && <div ref={setViewsSlot} className="ml-2 flex shrink-0 items-center gap-1" />}
          <div className="ml-auto flex items-center gap-2">
            {tab !== "wise" && (
              <>
                <Select
                  aria-label="Vendor"
                  value={vendorId ?? ""}
                  options={vendorOptions}
                  onChange={(e) => setSearch({ vendor: e.target.value || null })}
                  className="h-7 w-44 text-[12px]"
                />
                <Select
                  aria-label="Production run"
                  value={runId ?? ""}
                  options={runOptions}
                  onChange={(e) => setSearch({ run: e.target.value || null })}
                  className="h-7 w-44 text-[12px]"
                />
              </>
            )}
            <Link
              to="/bookkeeper"
              className="flex h-8 items-center gap-1.5 rounded-md px-2 text-[13px] whitespace-nowrap text-muted hover:bg-hover hover:text-text"
            >
              <FileArchive className="size-4" /> Bookkeeper pack
            </Link>
            {can("bill.create") && (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                New bill
              </Button>
            )}
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto bg-bg">
          {tab === "due" ? (
            <DueBoard vendorId={vendorId} runId={runId} />
          ) : tab === "bills" ? (
            <BillsList vendorId={vendorId} runId={runId} viewsSlot={viewsSlot} />
          ) : tab === "payments" ? (
            <PaymentsList vendorId={vendorId} runId={runId} viewsSlot={viewsSlot} />
          ) : (
            <WiseHistory />
          )}
        </div>
      </div>
      {creating && (
        <NewBillDialog
          vendorId={vendorId}
          onClose={(id) => {
            setCreating(false);
            setSearch({ new: null, ...(id ? { bill: id } : {}) });
          }}
        />
      )}
    </div>
  );
}

type BillRow = Bill & { vendor: string; due: string | null; paid: number; left: number; match: ReturnType<typeof billMatch>; pos: string; runs: string };

const STATUS_FILTERS = [
  { value: "open", label: "Not paid yet" },
  { value: "all", label: "All statuses" },
  ...BILL_STATUSES.map((s) => ({ value: s, label: BILL_STATUS_LABELS[s] })),
];

function BillsList({ vendorId, runId, viewsSlot }: { vendorId: string | null; runId: string | null; viewsSlot: HTMLElement | null }) {
  const openBill = usePaneRoot("bill"); // Side pane --- its row lit
  const ix = useBillIndex();
  const params = useSearch();
  const status = params.get("status") ?? "open";
  const rows = useMemo<BillRow[]>(
    () =>
      ix.d.bills
        .filter((b) => (status === "all" ? true : status === "open" ? b.status !== "paid" && b.status !== "void" : b.status === (status as BillStatus)))
        .filter((b) => !vendorId || b.vendor_id === vendorId)
        .filter((b) => !runId || billLinks(ix, b.id).runIds.includes(runId))
        .map((b) => {
          const st = billState(ix, b.id);
          const l = billLinks(ix, b.id);
          return {
            ...b,
            vendor: vendorLabelOf(ix.vendorById.get(b.vendor_id)),
            due: billDue(ix, b, dayOf).date,
            paid: st.paid,
            left: st.due + st.pending,
            match: billMatch(ix, b.id),
            pos: l.poIds.map((id) => ix.poById.get(id)?.number ?? "").join(" "),
            runs: l.runIds.map((id) => ix.runById.get(id)?.number ?? "").join(" "),
          };
        }),
    [ix, status, vendorId, runId],
  );
  const columns = useMemo<Column<BillRow>[]>(
    () => [
      {
        key: "number",
        header: "Bill",
        width: "92px",
        value: (b) => Number(b.number.replace(/\D/g, "")) || 0,
        cell: (b) => <span className="font-mono text-[12px] font-medium">{b.number}</span>,
      },
      { key: "vendor", header: "Vendor", width: "minmax(130px,1fr)" },
      { key: "invoice_number", header: "Their invoice", width: "140px", cell: (b) => <span className="font-mono text-[12px]">{b.invoice_number}</span> },
      { key: "kind", header: "Kind", width: "100px", value: (b) => BILL_KIND_LABELS[b.kind] },
      {
        key: "status",
        header: "Status",
        width: "104px",
        value: (b) => BILL_STATUSES.indexOf(b.status),
        cell: (b) => <Badge tone={BILL_STATUS_TONE[b.status]}>{BILL_STATUS_LABELS[b.status]}</Badge>,
      },
      {
        key: "match",
        header: "Match",
        width: "136px",
        value: (b) => b.match.status,
        cell: (b) => <Badge tone={MATCH_TONE[b.match.status]}>{MATCH_LABELS[b.match.status]}</Badge>,
      },
      { key: "bill_date", header: "Dated", width: "100px", cell: (b) => <span className="text-muted">{shortDate(b.bill_date)}</span> },
      { key: "due", header: "Due", width: "100px", cell: (b) => <span className="text-muted">{shortDate(b.due)}</span> },
      {
        key: "amount",
        header: "Amount",
        width: "112px",
        align: "right",
        cell: (b) => <span className="font-mono">{amount(Number(b.amount), b.currency)}</span>,
      },
      {
        key: "left",
        header: "Still to pay",
        width: "112px",
        align: "right",
        cell: (b) => <span className={cx("font-mono", b.left === 0 && "text-faint")}>{amount(b.left, b.currency)}</span>,
      },
      { key: "pos", header: "POs", width: "120px", cell: (b) => <span className="text-[12px] text-muted">{b.pos}</span> },
      { key: "runs", header: "Run", width: "80px", cell: (b) => <span className="text-[12px] text-muted">{b.runs}</span> },
    ],
    [],
  );
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-line bg-surface px-4 py-2 text-[12px] text-muted">
        <Select
          aria-label="Status"
          value={status}
          options={STATUS_FILTERS}
          onChange={(e) => setSearch({ status: e.target.value === "open" ? null : e.target.value })}
          className="h-7 w-40 text-[12px]"
        />
        <span>
          {rows.length} {rows.length === 1 ? "bill" : "bills"} · still to pay{" "}
          <span className="font-mono text-text">{amount(rows.filter((r) => r.currency === "USD").reduce((n, r) => n + r.left, 0))}</span>
        </span>
      </div>
      <div className="min-h-0 flex-1 bg-surface">
        <DataTable
          rows={rows}
          columns={columns}
          activeId={openBill}
          defaultSort={{ key: "number", desc: true }}
          onOpen={(b) => setSearch({ bill: b.id })}
          exportName="Bills"
          viewsSlot={viewsSlot}
          empty={<div className="p-6 text-center text-muted">No bills here. New bill adds one from its PDF or photo, or from a PO's Bills tab.</div>}
        />
      </div>
    </div>
  );
}

type PaymentRow = Payment & { vendor: string; bills: string; billIds: string[] };

function PaymentsList({ vendorId, runId, viewsSlot }: { vendorId: string | null; runId: string | null; viewsSlot: HTMLElement | null }) {
  const ix = useBillIndex();
  const rows = useMemo<PaymentRow[]>(
    () =>
      ix.d.payments
        .filter((p) => !vendorId || p.vendor_id === vendorId)
        .map((p) => {
          const bills = (ix.allocsByPayment.get(p.id) ?? []).map((a) => ix.billById.get(a.bill_id)).filter((b): b is Bill => !!b);
          return {
            ...p,
            vendor: vendorLabelOf(ix.vendorById.get(p.vendor_id ?? "")),
            bills: bills.map((b) => b.number).join(", "),
            billIds: bills.map((b) => b.id),
          };
        })
        .filter((p) => !runId || p.billIds.some((b) => billLinks(ix, b).runIds.includes(runId))),
    [ix, vendorId, runId],
  );
  const columns = useMemo<Column<PaymentRow>[]>(
    () => [
      {
        key: "number",
        header: "Payment",
        width: "96px",
        value: (p) => Number(p.number.replace(/\D/g, "")) || 0,
        cell: (p) => <span className="font-mono text-[12px] font-medium">{p.number}</span>,
      },
      { key: "paid_on", header: "Paid on", width: "104px", cell: (p) => <span className="text-muted">{shortDate(p.paid_on)}</span> },
      { key: "vendor", header: "Vendor", width: "minmax(130px,1fr)" },
      { key: "method", header: "How", width: "minmax(120px,1fr)", value: (p) => paymentWith(ix, p) }, // Which card ---
      {
        key: "status",
        header: "Status",
        width: "170px",
        value: (p) => p.status,
        cell: (p) => <Badge tone={PAYMENT_STATUS_TONE[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>,
      },
      {
        key: "amount",
        header: "Amount",
        width: "112px",
        align: "right",
        cell: (p) => <span className="font-mono">{amount(Number(p.amount), p.currency)}</span>,
      },
      {
        key: "fee",
        header: "Fee",
        width: "80px",
        align: "right",
        cell: (p) => <span className="font-mono text-muted">{Number(p.fee) ? amount(Number(p.fee), p.fee_currency || p.currency) : ""}</span>,
      },
      { key: "reference", header: "Reference", width: "minmax(160px,1fr)", cell: (p) => <span className="font-mono text-[12px]">{p.reference}</span> },
      { key: "bills", header: "Bills", width: "150px", cell: (p) => <span className="text-[12px] text-muted">{p.bills}</span> },
    ],
    [ix],
  );
  return (
    <div className="h-full bg-surface">
      <DataTable
        rows={rows}
        columns={columns}
        defaultSort={{ key: "number", desc: true }}
        onOpen={(p) => p.billIds[0] && setSearch({ bill: p.billIds[0] })}
        exportName="Payments"
        viewsSlot={viewsSlot}
        empty={<div className="p-6 text-center text-muted">No payments yet. Pay bills from Payments due, or link past Wise transfers in Wise history.</div>}
      />
    </div>
  );
}
